import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Download, FilePenLine, LogOut, RefreshCw, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  deleteAdminSessionById,
  exportAdminSessionsCsv,
  forceAdminSessionCheckout,
  getAdminSessionPromoters,
  getAdminSessions,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import { selectAuthLoading, selectUser } from "../../../redux/slices/authSlice";
import { DataTable } from "../Table";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import SessionAuditDrawer from "./SessionAuditDrawer";
import SessionCorrectionDrawer from "./SessionCorrectionDrawer";
import "./SessionsView.css";

const DEFAULT_FILTERS = {
  promoterIds: [],
  startDate: "",
  endDate: "",
  status: "all",
};

const DEFAULT_EXPORT_FILTERS = {
  promoterScope: "all",
  promoterIds: [],
  startDate: "",
  endDate: "",
  status: "all",
};

const DEFAULT_PAGINATION = {
  limit: 10,
  offset: 0,
  total: 0,
};
const DEFAULT_SERVER_SORT = {
  key: "checked_in_at",
  direction: "desc",
};
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const CHECKOUT_REASON_OPTIONS = [
  { value: "break", key: "admin.sessions.checkoutReason.break" },
  { value: "off_duty", key: "admin.sessions.checkoutReason.offDuty" },
];
const MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;

const formatPromoterLabel = (promoter) =>
  promoter?.email ? `${promoter.full_name} (${promoter.email})` : promoter?.full_name || "";

const toPositiveId = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const formatMinutes = (minutes) => {
  const safeMinutes = Number.parseInt(minutes || 0, 10) || 0;
  if (safeMinutes < 60) return `${safeMinutes}m`;
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
};

const formatSecondsAsDuration = (seconds) => {
  const safeSeconds = Math.max(0, Number.parseInt(seconds || 0, 10) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const formatMeters = (value) => {
  // Guard null/"" first: Number(null) === 0 would render a missing distance as "0 m".
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return null;
  if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(2)} km`;
  return `${Math.round(numericValue)} m`;
};

const formatDistanceSummary = (value) => {
  // Guard null/"" first: Number(null) === 0 would render a missing distance as "0 m away".
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return null;
  if (numericValue >= 1000) return `${Math.round(numericValue / 1000)} km away`;
  return `${Math.round(numericValue)} m away`;
};

const getEffectiveLocationMatchStatus = (session) => {
  const distanceMeters =
    session?.distance_from_outlet_meters != null
      ? Number.parseInt(session.distance_from_outlet_meters, 10)
      : null;
  const appliedRadiusMeters =
    session?.applied_radius_meters != null
      ? Number.parseInt(session.applied_radius_meters, 10)
      : null;
  const savedStatus = String(session?.location_match_status || "").trim().toLowerCase();

  if (
    Number.isInteger(distanceMeters) &&
    distanceMeters >= 0 &&
    Number.isInteger(appliedRadiusMeters) &&
    appliedRadiusMeters > 0
  ) {
    return distanceMeters <= appliedRadiusMeters ? "on_site" : "off_site";
  }

  return savedStatus || "unverified";
};

const toSessionTimestamp = (value, { clampFutureToNow = false } = {}) => {
  if (!value) return NaN;
  const parsed = new Date(value).getTime();
  if (!Number.isFinite(parsed)) return NaN;
  if (!clampFutureToNow) return parsed;

  const now = Date.now();
  if (parsed > now && parsed - now <= MAX_FUTURE_SKEW_MS) {
    return now;
  }
  return parsed;
};

const getCheckoutReasonKey = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "lunch" || normalized === "break") return "break";
  if (normalized === "off_duty") return "off_duty";
  if (normalized === "auto_checkout") return "auto_checkout";
  return null;
};

const toBooleanFlag = (value, fallbackText = null) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value > 0;
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "1" || normalized === "true" || normalized === "t") return true;
  if (normalized === "0" || normalized === "false" || normalized === "f") return false;
  return String(fallbackText || "").trim().toLowerCase() === "roadshow";
};

const shouldShowRoadshowDetails = (details) => {
  const normalizedDetails = String(details || "").trim();
  if (!normalizedDetails) return false;
  return normalizedDetails.toLowerCase() !== "roadshow";
};

const resolvePromoterFromOptions = (rawInput, options) => {
  const normalizedInput = String(rawInput || "").trim();
  if (!normalizedInput) return null;

  const normalizedLower = normalizedInput.toLowerCase();
  const list = Array.isArray(options) ? options : [];

  const exactMatch =
    list.find((promoter) => {
      const helperValue = formatPromoterLabel(promoter).toLowerCase();
      const fullName = String(promoter.full_name || "").trim().toLowerCase();
      const email = String(promoter.email || "").trim().toLowerCase();
      return (
        helperValue === normalizedLower ||
        fullName === normalizedLower ||
        email === normalizedLower
      );
    }) || null;

  if (exactMatch) return exactMatch;

  const partialMatches = list.filter((promoter) => {
    const helperValue = formatPromoterLabel(promoter).toLowerCase();
    const fullName = String(promoter.full_name || "").toLowerCase();
    const email = String(promoter.email || "").toLowerCase();
    return (
      helperValue.includes(normalizedLower) ||
      fullName.includes(normalizedLower) ||
      email.includes(normalizedLower)
    );
  });

  return partialMatches.length === 1 ? partialMatches[0] : null;
};

export default function SessionsView({
  user = null,
  searchIntent = null,
  onSearchIntentHandled = null,
}) {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const authenticatedUser = useSelector(selectUser);
  const authLoading = useSelector(selectAuthLoading);
  const locale = i18n.language === "ms" ? "ms-MY" : "en-MY";
  const effectiveUser = authenticatedUser || user;

  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [serverSort, setServerSort] = useState(DEFAULT_SERVER_SORT);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState(DEFAULT_EXPORT_FILTERS);
  const [checkoutInProgressById, setCheckoutInProgressById] = useState({});
  const [checkoutConfirmSession, setCheckoutConfirmSession] = useState(null);
  const [checkoutReason, setCheckoutReason] = useState("");
  const [auditDrawerSession, setAuditDrawerSession] = useState(null);
  const [correctionSessionId, setCorrectionSessionId] = useState(null);
  const [deleteSessionTarget, setDeleteSessionTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [promoterInput, setPromoterInput] = useState("");
  const [promoterOptions, setPromoterOptions] = useState([]);
  const [selectedPromoterOptions, setSelectedPromoterOptions] = useState([]);
  const [promoterOptionsLoading, setPromoterOptionsLoading] = useState(false);
  const [showPromoterSuggestions, setShowPromoterSuggestions] = useState(false);
  const promoterHelperRef = useRef(null);
  const [exportPromoterInput, setExportPromoterInput] = useState("");
  const [exportPromoterOptions, setExportPromoterOptions] = useState([]);
  const [selectedExportPromoters, setSelectedExportPromoters] = useState([]);
  const [exportPromoterOptionsLoading, setExportPromoterOptionsLoading] = useState(false);
  const [showExportPromoterSuggestions, setShowExportPromoterSuggestions] = useState(false);
  const exportPromoterHelperRef = useRef(null);
  const [durationTick, setDurationTick] = useState(Date.now());
  const [tableSearchQuery, setTableSearchQuery] = useState("");

  useEffect(() => {
    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setPromoterOptionsLoading(true);
        const response = await getAdminSessionPromoters({
          q: promoterInput,
          limit: 20,
        });
        if (!isCancelled) {
          setPromoterOptions(response?.data || []);
        }
      } catch (error) {
        if (!isCancelled) {
          setPromoterOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setPromoterOptionsLoading(false);
        }
      }
    }, 220);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [promoterInput]);

  useEffect(() => {
    if (!showExportModal || exportFilters.promoterScope !== "selected") return undefined;

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setExportPromoterOptionsLoading(true);
        const response = await getAdminSessionPromoters({
          q: exportPromoterInput,
          limit: 20,
        });
        if (!isCancelled) {
          setExportPromoterOptions(response?.data || []);
        }
      } catch (error) {
        if (!isCancelled) {
          setExportPromoterOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setExportPromoterOptionsLoading(false);
        }
      }
    }, 220);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [showExportModal, exportFilters.promoterScope, exportPromoterInput]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (promoterHelperRef.current && !promoterHelperRef.current.contains(event.target)) {
        setShowPromoterSuggestions(false);
      }
      if (
        exportPromoterHelperRef.current &&
        !exportPromoterHelperRef.current.contains(event.target)
      ) {
        setShowExportPromoterSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const hasActiveSessions = sessions.some(
      (session) => session?.status === "active" && session?.checked_in_at,
    );
    if (!hasActiveSessions) return undefined;

    const intervalId = setInterval(() => {
      setDurationTick(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [sessions]);

  const refreshSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAdminSessions({
        ...appliedFilters,
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: serverSort.key,
        sortDirection: serverSort.direction,
      });

      setSessions(response?.data || []);
      setPagination((previous) => ({
        ...previous,
        total: response?.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching sessions:", error);
      showError(t("admin.sessions.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters,
    pagination.limit,
    pagination.offset,
    serverSort.key,
    serverSort.direction,
    showError,
    t,
  ]);

  useEffect(() => {
    refreshSessions();
  }, [
    refreshSessions,
  ]);

  useEffect(() => {
    if (!searchIntent) return;

    const item = searchIntent.item || null;
    const query = String(searchIntent.query || "").trim();
    const promoterId = toPositiveId(item?.user_id || item?.id);
    const promoterName = String(item?.promoter_name || item?.label || "").trim();
    const promoterEmail = String(item?.promoter_email || item?.email || "").trim();
    const nextStatus =
      item?.status === "active" || item?.status === "checked_out"
        ? item.status
        : "all";

    if (promoterId) {
      const promoterOption = {
        user_id: promoterId,
        full_name: promoterName || promoterEmail || `Promoter #${promoterId}`,
        email: promoterEmail,
      };

      setSelectedPromoterOptions([promoterOption]);
      setPromoterInput(formatPromoterLabel(promoterOption));
      setDraftFilters((previous) => ({
        ...previous,
        promoterIds: [promoterId],
        status: nextStatus,
      }));
      setAppliedFilters((previous) => ({
        ...previous,
        promoterIds: [promoterId],
        status: nextStatus,
      }));
      setTableSearchQuery("");
    } else {
      setSelectedPromoterOptions([]);
      setPromoterInput("");
      setDraftFilters((previous) => ({
        ...previous,
        promoterIds: [],
        status: "all",
      }));
      setAppliedFilters((previous) => ({
        ...previous,
        promoterIds: [],
        status: "all",
      }));
      setTableSearchQuery(query);
    }

    setShowPromoterSuggestions(false);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));

    if (typeof onSearchIntentHandled === "function" && searchIntent.intentId) {
      onSearchIntentHandled(searchIntent.intentId);
    }
  }, [searchIntent, onSearchIntentHandled]);

  const selectedPromoters = selectedPromoterOptions;

  const filteredSessions = useMemo(() => {
    const normalizedSearch = String(tableSearchQuery || "").trim().toLowerCase();
    if (!normalizedSearch) return sessions;

    return sessions.filter((session) => {
      const promoterName = String(session.promoter_name || "").toLowerCase();
      const promoterEmail = String(session.promoter_email || "").toLowerCase();
      const outletName = String(session.outlet_name || "").toLowerCase();
      const eventDetails = String(session.event_details || "").toLowerCase();
      const roadshowLabel = toBooleanFlag(session.is_event, session.event_details)
        ? String(t("admin.sessions.roadshow.label")).toLowerCase()
        : "";
      return (
        promoterName.includes(normalizedSearch) ||
        promoterEmail.includes(normalizedSearch) ||
        outletName.includes(normalizedSearch) ||
        eventDetails.includes(normalizedSearch) ||
        roadshowLabel.includes(normalizedSearch)
      );
    });
  }, [tableSearchQuery, sessions, t]);

  const addPromoterFilter = (promoter) => {
    const promoterId = String(promoter.user_id);
    setDraftFilters((previous) => {
      if (previous.promoterIds.map(String).includes(promoterId)) return previous;
      const nextPromoterIds = [...previous.promoterIds, promoter.user_id];
      setAppliedFilters((appliedPrevious) => ({
        ...appliedPrevious,
        promoterIds: nextPromoterIds,
      }));
      return {
        ...previous,
        promoterIds: nextPromoterIds,
      };
    });
    setSelectedPromoterOptions((previous) => {
      if (previous.some((item) => String(item.user_id) === promoterId)) return previous;
      return [...previous, promoter];
    });
    setPromoterInput("");
    setShowPromoterSuggestions(false);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const removePromoterFilter = (promoterId) => {
    setDraftFilters((previous) => {
      const nextPromoterIds = previous.promoterIds.filter((id) => String(id) !== String(promoterId));
      setAppliedFilters((appliedPrevious) => ({
        ...appliedPrevious,
        promoterIds: nextPromoterIds,
      }));
      return {
        ...previous,
        promoterIds: nextPromoterIds,
      };
    });
    setSelectedPromoterOptions((previous) =>
      previous.filter((item) => String(item.user_id) !== String(promoterId)),
    );
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const clearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setSelectedPromoterOptions([]);
    setPromoterInput("");
    setTableSearchQuery("");
    setShowPromoterSuggestions(false);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const handleStatusChange = (nextStatus) => {
    setDraftFilters((previous) => ({
      ...previous,
      status: nextStatus,
    }));
    setAppliedFilters((previous) => ({
      ...previous,
      status: nextStatus,
    }));
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const handleDateChange = (fieldName, value) => {
    setDraftFilters((previous) => ({
      ...previous,
      [fieldName]: value,
    }));
    setAppliedFilters((previous) => ({
      ...previous,
      [fieldName]: value,
    }));
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const handlePromoterInputEnter = async () => {
    const trimmedInput = String(promoterInput || "").trim();
    if (!trimmedInput) {
      return;
    }

    const localMatch = resolvePromoterFromOptions(trimmedInput, promoterOptions);
    if (localMatch) {
      addPromoterFilter(localMatch);
      return;
    }

    try {
      setPromoterOptionsLoading(true);
      const response = await getAdminSessionPromoters({ q: trimmedInput, limit: 20 });
      const remoteOptions = response?.data || [];
      setPromoterOptions(remoteOptions);
      const remoteMatch = resolvePromoterFromOptions(trimmedInput, remoteOptions);
      if (remoteMatch) {
        addPromoterFilter(remoteMatch);
      }
    } catch (error) {
      console.error("Error resolving promoter input:", error);
    } finally {
      setPromoterOptionsLoading(false);
    }
  };

  const handleCheckout = (session) => {
    if (!session || session.status !== "active") return;
    setCheckoutReason("");
    setCheckoutConfirmSession(session);
  };

  const closeCheckoutConfirm = () => {
    const currentCheckInId = checkoutConfirmSession?.check_in_id;
    if (currentCheckInId && checkoutInProgressById[currentCheckInId]) return;
    setCheckoutReason("");
    setCheckoutConfirmSession(null);
  };

  const handleCheckoutConfirm = async () => {
    if (!checkoutConfirmSession || checkoutConfirmSession.status !== "active") return;
    if (!checkoutReason) {
      showError(t("admin.sessions.checkoutReason.validation"));
      return;
    }
    const currentSession = checkoutConfirmSession;

    try {
      setCheckoutInProgressById((previous) => ({
        ...previous,
        [currentSession.check_in_id]: true,
      }));

      await forceAdminSessionCheckout(currentSession.check_in_id, {
        checkout_reason: checkoutReason,
      });
      showSuccess(t("admin.sessions.checkoutSuccess"));

      const response = await getAdminSessions({
        ...appliedFilters,
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: serverSort.key,
        sortDirection: serverSort.direction,
      });
      setSessions(response?.data || []);
      setPagination((previous) => ({
        ...previous,
        total: response?.pagination?.total || previous.total,
      }));
      setCheckoutConfirmSession(null);
    } catch (error) {
      console.error("Error forcing checkout:", error);
      showError(error?.response?.data?.error || t("admin.sessions.checkoutFailed"));
    } finally {
      setCheckoutInProgressById((previous) => ({
        ...previous,
        [currentSession.check_in_id]: false,
      }));
    }
  };

  const handleExport = async () => {
    if (exportFilters.promoterScope === "selected" && exportFilters.promoterIds.length === 0) {
      showError(t("admin.sessions.exportValidation.selectPromoter"));
      return;
    }

    const hasStartDate = Boolean(exportFilters.startDate);
    const hasEndDate = Boolean(exportFilters.endDate);
    if (hasStartDate !== hasEndDate) {
      showError(t("admin.sessions.exportValidation.selectBothDates"));
      return;
    }
    if (hasStartDate && hasEndDate && exportFilters.endDate < exportFilters.startDate) {
      showError(t("admin.sessions.exportValidation.endDateBeforeStart"));
      return;
    }

    const exportParams = {};
    if (exportFilters.promoterScope === "selected") {
      exportParams.promoterIds = exportFilters.promoterIds;
    }
    if (hasStartDate && hasEndDate) {
      exportParams.startDate = exportFilters.startDate;
      exportParams.endDate = exportFilters.endDate;
    }
    if (exportFilters.status && exportFilters.status !== "all") {
      exportParams.status = exportFilters.status;
    }

    try {
      setExporting(true);
      const response = await exportAdminSessionsCsv(exportParams);

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const today = new Date().toISOString().split("T")[0];
      link.href = blobUrl;
      link.setAttribute("download", `session_history_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      showSuccess(t("admin.sessions.exportSuccess"));
      setShowExportModal(false);
    } catch (error) {
      console.error("Error exporting sessions CSV:", error);
      showError(error?.response?.data?.error || t("admin.sessions.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const openExportModal = () => {
    setExportFilters(DEFAULT_EXPORT_FILTERS);
    setSelectedExportPromoters([]);
    setExportPromoterInput("");
    setExportPromoterOptions([]);
    setShowExportPromoterSuggestions(false);
    setShowExportModal(true);
  };

  const handleExportScopeChange = (scope) => {
    setExportFilters((previous) => ({
      ...previous,
      promoterScope: scope,
      promoterIds: scope === "selected" ? previous.promoterIds : [],
    }));
    if (scope !== "selected") {
      setSelectedExportPromoters([]);
      setExportPromoterInput("");
      setShowExportPromoterSuggestions(false);
    }
  };

  const addExportPromoter = (promoter) => {
    const promoterId = String(promoter.user_id);
    setExportFilters((previous) => {
      if (previous.promoterIds.map(String).includes(promoterId)) return previous;
      return {
        ...previous,
        promoterIds: [...previous.promoterIds, promoter.user_id],
      };
    });
    setSelectedExportPromoters((previous) => {
      if (previous.some((item) => String(item.user_id) === promoterId)) return previous;
      return [...previous, promoter];
    });
    setExportPromoterInput("");
    setShowExportPromoterSuggestions(false);
  };

  const removeExportPromoter = (promoterId) => {
    setExportFilters((previous) => ({
      ...previous,
      promoterIds: previous.promoterIds.filter((id) => String(id) !== String(promoterId)),
    }));
    setSelectedExportPromoters((previous) =>
      previous.filter((item) => String(item.user_id) !== String(promoterId)),
    );
  };

  const handleExportPromoterEnter = async () => {
    const trimmedInput = String(exportPromoterInput || "").trim();
    if (!trimmedInput) return;

    const localMatch = resolvePromoterFromOptions(trimmedInput, exportPromoterOptions);
    if (localMatch) {
      addExportPromoter(localMatch);
      return;
    }

    try {
      setExportPromoterOptionsLoading(true);
      const response = await getAdminSessionPromoters({ q: trimmedInput, limit: 20 });
      const remoteOptions = response?.data || [];
      setExportPromoterOptions(remoteOptions);
      const remoteMatch = resolvePromoterFromOptions(trimmedInput, remoteOptions);
      if (remoteMatch) {
        addExportPromoter(remoteMatch);
      }
    } catch (error) {
      console.error("Error resolving export promoter input:", error);
    } finally {
      setExportPromoterOptionsLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return t("admin.sessions.na");
    const timestamp = toSessionTimestamp(value, { clampFutureToNow: true });
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return t("admin.sessions.na");
    return date.toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTimeParts = (value) => {
    if (!value) return null;
    const timestamp = toSessionTimestamp(value, { clampFutureToNow: true });
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;

    return {
      date: date.toLocaleDateString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      time: date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const renderDateTimeCell = (value) => {
    const parts = formatDateTimeParts(value);
    if (!parts) {
      return <span className="admin-sessions-datetime-empty">{t("admin.sessions.na")}</span>;
    }

    return (
      <div className="admin-sessions-datetime-cell">
        <div className="admin-sessions-datetime-line">
          <span className="admin-sessions-datetime-title">
            {t("admin.common.date", { defaultValue: "Date" })}
          </span>
          <span>{parts.date}</span>
        </div>
        <div className="admin-sessions-datetime-line">
          <span className="admin-sessions-datetime-title">
            {t("admin.common.time", { defaultValue: "Time" })}
          </span>
          <span>{parts.time}</span>
        </div>
      </div>
    );
  };

  const formatLocationMatchLabel = (value) => {
    switch (String(value || "").trim().toLowerCase()) {
      case "on_site":
        return t("admin.sessions.locationAudit.status.onSite");
      case "off_site":
        return t("admin.sessions.locationAudit.status.offSite");
      default:
        return t("admin.sessions.locationAudit.status.unverified");
    }
  };

  const getSessionDurationLabel = (session) => {
    if (!session) return formatMinutes(0);

    const durationSeconds =
      Number.parseInt(session.duration_seconds || 0, 10) || 0;
    const accumulatedDurationSeconds =
      Number.parseInt(session.accumulated_duration_seconds || 0, 10) || 0;
    const activeSegmentStartedAtMs = toSessionTimestamp(
      session.active_segment_started_at || session.checked_in_at,
      { clampFutureToNow: true },
    );

    if (session.status === "active" && Number.isFinite(activeSegmentStartedAtMs)) {
      const elapsedSeconds =
        accumulatedDurationSeconds +
        Math.floor((durationTick - activeSegmentStartedAtMs) / 1000);
      return formatSecondsAsDuration(elapsedSeconds);
    }

    if (durationSeconds > 0 || session.status === "checked_out") {
      return formatSecondsAsDuration(durationSeconds);
    }

    return formatMinutes(session.duration_minutes);
  };

  const columns = useMemo(
    () => [
      {
        label: t("admin.sessions.promoter"),
        sortKey: "promoter_name",
        sortValue: (session) => String(session?.promoter_name || "").toLowerCase(),
      },
      {
        label: t("admin.sessions.outlet"),
        sortKey: "outlet_name",
        sortValue: (session) => String(session?.outlet_name || "").toLowerCase(),
      },
      {
        label: t("admin.sessions.locationAudit.title"),
      },
      {
        label: t("admin.sessions.details"),
      },
      {
        label: t("admin.sessions.checkIn"),
        sortKey: "checked_in_at",
        sortValue: (session) => {
          const value = toSessionTimestamp(session?.checked_in_at, {
            clampFutureToNow: true,
          });
          return Number.isFinite(value) ? value : 0;
        },
      },
      {
        label: t("admin.sessions.checkOut"),
        sortKey: "checked_out_at",
        sortValue: (session) => {
          const value = toSessionTimestamp(session?.checked_out_at, {
            clampFutureToNow: true,
          });
          return Number.isFinite(value) ? value : 0;
        },
      },
      {
        label: t("admin.sessions.duration"),
        sortKey: "duration",
        sortValue: (session) => (Number.parseInt(session?.duration_seconds, 10) || 0) * 1000,
      },
      {
        label: t("admin.sessions.checkoutReason.label"),
      },
      {
        label: t("admin.common.status"),
        sortKey: "status",
        sortValue: (session) => (session?.status === "active" ? 0 : 1),
      },
      { label: t("admin.sessions.action") },
    ],
    [t],
  );

  const handleServerPaginationChange = ({ limit, offset }) => {
    setPagination((previous) => ({
      ...previous,
      limit,
      offset,
    }));
  };

  const handleServerSortChange = useCallback(
    ({ key, direction }) => {
      const hasValidSort = Boolean(key) && Boolean(direction);
      const normalizedDirection = String(direction || "").toLowerCase();
      const nextSort = hasValidSort && (normalizedDirection === "asc" || normalizedDirection === "desc")
        ? { key, direction: normalizedDirection }
        : DEFAULT_SERVER_SORT;

      setServerSort(nextSort);
      setPagination((previous) => ({
        ...previous,
        offset: 0,
      }));
    },
    [],
  );

  const handleItemsPerPageChange = (event) => {
    const nextLimit = Number.parseInt(event.target.value, 10) || DEFAULT_PAGINATION.limit;
    setPagination((previous) => ({
      ...previous,
      limit: nextLimit,
      offset: 0,
    }));
  };

  const handleDeleteSessionConfirm = async () => {
    if (!deleteSessionTarget?.check_in_id) return;

    try {
      setDeleteSubmitting(true);
      await deleteAdminSessionById(deleteSessionTarget.check_in_id);
      showSuccess(
        t("admin.sessions.corrections.deleteSuccess", {
          defaultValue: "Session deleted successfully.",
        }),
      );
      setDeleteSessionTarget(null);
      await refreshSessions();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.sessions.corrections.deleteFailed", {
            defaultValue: "Failed to delete session.",
          }),
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const renderSessionRow = (session) => {
    const checkoutReasonKey = getCheckoutReasonKey(session.checkout_reason);
    const effectiveLocationStatus = getEffectiveLocationMatchStatus(session);
    const locationStatusClass = effectiveLocationStatus;
    const distanceSummaryLabel = formatDistanceSummary(
      session.distance_from_outlet_meters,
    );
    const isRoadshow = toBooleanFlag(session.is_event, session.event_details);
    const roadshowDetails = String(session.event_details || "").trim();
    const isSuperAdmin = effectiveUser?.role === "super_admin";
    const hasCheckoutAction = session.status === "active";
    const hasActionButtons = hasCheckoutAction || isSuperAdmin;
    return (
      <tr key={session.check_in_id}>
      <td>
        <div className="admin-sessions-promoter">
          <span className="admin-sessions-promoter-name">{session.promoter_name}</span>
          <span className="admin-sessions-promoter-email">{session.promoter_email}</span>
        </div>
      </td>
      <td>
        <div className="admin-sessions-outlet">
          <span className="admin-sessions-outlet-name">
            {session.outlet_name || t("admin.sessions.na")}
          </span>
          {session.outlet_address ? (
            <span className="admin-sessions-outlet-address">
              {session.outlet_address}
            </span>
          ) : null}
        </div>
      </td>
      <td>
        <div className="admin-sessions-location-audit">
          <span
            className={`admin-sessions-location-badge is-${locationStatusClass}`}
          >
            {formatLocationMatchLabel(effectiveLocationStatus)}
          </span>
          <span className="admin-sessions-location-summary">
            {distanceSummaryLabel || t("admin.sessions.na")}
          </span>
          <button
            type="button"
            className="admin-sessions-location-toggle"
            onClick={() =>
              setAuditDrawerSession({
                ...session,
                location_match_status: effectiveLocationStatus,
              })
            }
          >
            {t("admin.sessions.locationAudit.viewDetails")}
          </button>
        </div>
      </td>
      <td>
        <div className="admin-sessions-event-cell">
          <span
            className={`admin-sessions-event-badge ${isRoadshow ? "is-roadshow" : "is-outlet"}`}
          >
            {isRoadshow ? "ROADSHOW" : "OUTLET VISIT"}
          </span>
          {isRoadshow && shouldShowRoadshowDetails(roadshowDetails) ? (
            <span className="admin-sessions-event-details">{roadshowDetails}</span>
          ) : null}
        </div>
      </td>
      <td>{renderDateTimeCell(session.checked_in_at)}</td>
      <td>{renderDateTimeCell(session.checked_out_at)}</td>
      <td>{getSessionDurationLabel(session)}</td>
      <td>
        {session.status === "active" ? (
          <span className="admin-checkout-reason-empty">{t("admin.sessions.na")}</span>
        ) : checkoutReasonKey ? (
          <span className={`admin-checkout-reason-badge is-${checkoutReasonKey}`}>
            {checkoutReasonKey === "break"
              ? t("admin.sessions.checkoutReason.break")
              : checkoutReasonKey === "auto_checkout"
                ? t("admin.sessions.checkoutReason.autoCheckout")
                : t("admin.sessions.checkoutReason.offDuty")}
          </span>
        ) : (
          <span className="admin-checkout-reason-empty">
            {t("admin.sessions.checkoutReason.notSet")}
          </span>
        )}
      </td>
      <td>
        <span
          className={`admin-status-badge ${
            session.status === "active" ? "admin-status-active" : "admin-status-checked-out"
          }`}
        >
          {session.status === "active"
            ? t("admin.common.active")
            : t("admin.dashboard.checkedOut")}
        </span>
      </td>
      <td>
        {hasActionButtons ? (
          <div
            className={`admin-sessions-row-actions ${
              !isSuperAdmin && hasCheckoutAction ? "admin-sessions-row-actions-compact" : ""
            }`}
          >
            {hasCheckoutAction ? (
              <button
                type="button"
                className="admin-sessions-checkout-btn admin-sessions-inline-action"
                title={t("admin.dashboard.checkOut")}
                aria-label={t("admin.dashboard.checkOut")}
                disabled={Boolean(checkoutInProgressById[session.check_in_id])}
                onClick={() => handleCheckout(session)}
              >
                {checkoutInProgressById[session.check_in_id] ? (
                  <RefreshCw size={14} className="admin-sessions-checkout-icon-spinning" />
                ) : (
                  <LogOut size={14} />
                )}
                {t("admin.dashboard.checkOut")}
              </button>
            ) : null}
            {isSuperAdmin ? (
              <>
                <button
                  type="button"
                  className="admin-btn-secondary admin-sessions-inline-action"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCorrectionSessionId(session.check_in_id);
                  }}
                >
                  <FilePenLine size={14} />
                  {t("admin.common.edit", { defaultValue: "Edit" })}
                </button>
                <button
                  type="button"
                  className="admin-btn-danger admin-sessions-inline-action"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteSessionTarget(session);
                  }}
                >
                  <Trash2 size={14} />
                  {t("admin.common.delete", { defaultValue: "Delete" })}
                </button>
              </>
            ) : null}
          </div>
        ) : (
          <span className="admin-sessions-no-action">-</span>
        )}
      </td>
      </tr>
    );
  };

  return (
    <div className="admin-sessions-content">
      <div className="admin-sessions-header">
        <div className="admin-sessions-header-left">
          <h2 className="admin-section-title">{t("admin.sessions.navTitle")}</h2>
          <p className="admin-section-subtitle">{t("admin.sessions.subtitle")}</p>
        </div>
        <div className="admin-sessions-actions">
          <button
            type="button"
            className="admin-btn-primary sessions-export-btn"
            onClick={openExportModal}
            disabled={exporting}
          >
            <Download size={16} />
            {exporting ? t("admin.sessions.exporting") : t("admin.sessions.export")}
          </button>
          <button
            type="button"
            className="admin-btn-secondary sessions-refresh-btn"
            onClick={refreshSessions}
            disabled={loading}
          >
            <RefreshCw size={16} />
            {t("admin.common.refresh")}
          </button>
        </div>
      </div>

      <div className="admin-sessions-filters">
        <div className="admin-sessions-filter-row">
          <div className="admin-sessions-field">
            <label>{t("admin.sessions.promoterFilter")}</label>
            <div className="admin-sessions-helper" ref={promoterHelperRef}>
              <input
                type="text"
                value={promoterInput}
                className="admin-sessions-input"
                placeholder={t("admin.sessions.searchPromoter")}
                onFocus={() => setShowPromoterSuggestions(true)}
                onChange={(event) => {
                  setPromoterInput(event.target.value);
                  setShowPromoterSuggestions(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setShowPromoterSuggestions(false);
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    handlePromoterInputEnter();
                  }
                }}
              />
              {showPromoterSuggestions && (
                <div className="admin-sessions-suggestions">
                  {promoterOptionsLoading ? (
                    <div className="admin-sessions-suggestion-empty">
                      {t("admin.common.loading")}
                    </div>
                  ) : promoterOptions.length > 0 ? (
                    promoterOptions.map((promoter) => (
                      <button
                        key={promoter.user_id}
                        type="button"
                        className="admin-sessions-suggestion-item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addPromoterFilter(promoter);
                        }}
                      >
                        {formatPromoterLabel(promoter)}
                      </button>
                    ))
                  ) : (
                    <div className="admin-sessions-suggestion-empty">
                      {t("admin.sessions.noPromotersFound")}
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedPromoters.length > 0 && (
              <div className="admin-sessions-chips">
                {selectedPromoters.map((promoter) => (
                  <button
                    key={`promoter-chip-${promoter.user_id}`}
                    type="button"
                    className="admin-sessions-chip"
                    onClick={() => removePromoterFilter(promoter.user_id)}
                  >
                    {formatPromoterLabel(promoter)}
                    <X size={14} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="admin-sessions-field">
            <label>{t("admin.sessions.startDate")}</label>
            <input
              type="date"
              className="admin-sessions-input"
              value={draftFilters.startDate}
              onChange={(event) => handleDateChange("startDate", event.target.value)}
            />
          </div>

          <div className="admin-sessions-field">
            <label>{t("admin.sessions.endDate")}</label>
            <input
              type="date"
              className="admin-sessions-input"
              value={draftFilters.endDate}
              onChange={(event) => handleDateChange("endDate", event.target.value)}
            />
          </div>

          <div className="admin-sessions-field">
            <label>{t("admin.common.status")}</label>
            <select
              className="admin-sessions-input admin-sessions-select"
              value={draftFilters.status}
              onChange={(event) => handleStatusChange(event.target.value)}
            >
              <option value="all">{t("admin.sessions.statusAll")}</option>
              <option value="active">{t("admin.common.active")}</option>
              <option value="checked_out">{t("admin.dashboard.checkedOut")}</option>
            </select>
          </div>
        </div>

        <div className="admin-sessions-filter-actions">
          <button type="button" className="admin-btn-secondary" onClick={clearFilters}>
            {t("admin.common.clear")}
          </button>
          <div className="admin-sessions-filter-tools">
            <div className="admin-sessions-items-per-page">
              <label htmlFor="sessionsItemsPerPage">{t("admin.common.itemsPerPage")}</label>
              <select
                id="sessionsItemsPerPage"
                className="admin-sessions-input admin-sessions-select"
                value={pagination.limit}
                onChange={handleItemsPerPageChange}
                disabled={loading}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <DataTable
        data={filteredSessions}
        columns={columns}
        renderRow={renderSessionRow}
        showHeader={false}
        showSearch={false}
        key={`sessions-table-${effectiveUser?.role || "unknown"}`}
        loading={loading || authLoading}
        serverMode
        defaultSortKey="checked_in_at"
        defaultSortDirection="desc"
        serverSort={serverSort}
        onServerSortChange={handleServerSortChange}
        serverPagination={{
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
        }}
        onServerPaginationChange={handleServerPaginationChange}
        containerClassName="admin-users-section admin-sessions-table-card"
        tableClassName="admin-users-table admin-sessions-table"
        emptyMessage={t("admin.sessions.noData")}
      />

      <div className="admin-sessions-pagination-meta">
        <span>
          {t("admin.sessions.showing", {
            start: pagination.total === 0 ? 0 : pagination.offset + 1,
            end: Math.min(pagination.offset + pagination.limit, pagination.total),
            total: pagination.total,
          })}
        </span>
      </div>

      <Modal
        isOpen={showExportModal}
        onClose={() => !exporting && setShowExportModal(false)}
        size="medium"
      >
        <div className="admin-sessions-export-modal">
          <h3 className="admin-sessions-export-title">{t("admin.sessions.export")}</h3>
          <p className="admin-sessions-export-subtitle">
            {t("admin.sessions.exportModal.subtitle")}
          </p>

          <div className="admin-sessions-export-section">
            <span className="admin-sessions-export-label">
              {t("admin.sessions.exportModal.promoterScope")}
            </span>
            <div className="admin-sessions-export-scope">
              <label>
                <input
                  type="radio"
                  name="exportPromoterScope"
                  value="all"
                  checked={exportFilters.promoterScope === "all"}
                  onChange={() => handleExportScopeChange("all")}
                />
                {t("admin.sessions.exportModal.allPromoters")}
              </label>
              <label>
                <input
                  type="radio"
                  name="exportPromoterScope"
                  value="selected"
                  checked={exportFilters.promoterScope === "selected"}
                  onChange={() => handleExportScopeChange("selected")}
                />
                {t("admin.sessions.exportModal.selectedPromoters")}
              </label>
            </div>
          </div>

          {exportFilters.promoterScope === "selected" && (
            <div className="admin-sessions-export-section">
              <div className="admin-sessions-helper" ref={exportPromoterHelperRef}>
                <input
                  type="text"
                  value={exportPromoterInput}
                  className="admin-sessions-input"
                  placeholder={t("admin.sessions.searchPromoter")}
                  onFocus={() => setShowExportPromoterSuggestions(true)}
                  onChange={(event) => {
                    setExportPromoterInput(event.target.value);
                    setShowExportPromoterSuggestions(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setShowExportPromoterSuggestions(false);
                    } else if (event.key === "Enter") {
                      event.preventDefault();
                      handleExportPromoterEnter();
                    }
                  }}
                />
                {showExportPromoterSuggestions && (
                  <div className="admin-sessions-suggestions">
                    {exportPromoterOptionsLoading ? (
                      <div className="admin-sessions-suggestion-empty">{t("admin.common.loading")}</div>
                    ) : exportPromoterOptions.length > 0 ? (
                      exportPromoterOptions.map((promoter) => (
                        <button
                          key={`export-${promoter.user_id}`}
                          type="button"
                          className="admin-sessions-suggestion-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            addExportPromoter(promoter);
                          }}
                        >
                          {formatPromoterLabel(promoter)}
                        </button>
                      ))
                    ) : (
                      <div className="admin-sessions-suggestion-empty">
                        {t("admin.sessions.noPromotersFound")}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedExportPromoters.length > 0 && (
                <div className="admin-sessions-chips">
                  {selectedExportPromoters.map((promoter) => (
                    <button
                      key={`export-chip-${promoter.user_id}`}
                      type="button"
                      className="admin-sessions-chip"
                      onClick={() => removeExportPromoter(promoter.user_id)}
                    >
                      {formatPromoterLabel(promoter)}
                      <X size={14} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="admin-sessions-export-dates">
            <div className="admin-sessions-field">
              <label>{t("admin.sessions.startDate")}</label>
              <input
                type="date"
                className="admin-sessions-input"
                value={exportFilters.startDate}
                onChange={(event) =>
                  setExportFilters((previous) => ({
                    ...previous,
                    startDate: event.target.value,
                  }))
                }
              />
            </div>
            <div className="admin-sessions-field">
              <label>{t("admin.sessions.endDate")}</label>
              <input
                type="date"
                className="admin-sessions-input"
                value={exportFilters.endDate}
                onChange={(event) =>
                  setExportFilters((previous) => ({
                    ...previous,
                    endDate: event.target.value,
                  }))
                }
              />
            </div>
            <div className="admin-sessions-field">
              <label>{t("admin.common.status")}</label>
              <select
                className="admin-sessions-input admin-sessions-select"
                value={exportFilters.status}
                onChange={(event) =>
                  setExportFilters((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
              >
                <option value="all">{t("admin.sessions.statusAll")}</option>
                <option value="active">{t("admin.common.active")}</option>
                <option value="checked_out">{t("admin.dashboard.checkedOut")}</option>
              </select>
            </div>
          </div>

          <div className="admin-sessions-export-actions">
            <button
              type="button"
              className="admin-btn-secondary"
              disabled={exporting}
              onClick={() => setShowExportModal(false)}
            >
              {t("admin.common.cancel")}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              disabled={exporting}
              onClick={handleExport}
            >
              {exporting ? t("admin.sessions.exporting") : t("admin.sessions.export")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(checkoutConfirmSession)}
        onClose={closeCheckoutConfirm}
        size="small"
      >
        <div className="admin-sessions-checkout-modal">
          <h3 className="admin-sessions-checkout-title">{t("admin.sessions.checkoutReason.title")}</h3>
          <p className="admin-sessions-checkout-subtitle">
            {t("admin.sessions.confirmCheckout", {
              name: checkoutConfirmSession?.promoter_name || "-",
            })}
          </p>
          <p className="admin-sessions-checkout-subtitle">
            {t("admin.sessions.checkoutReason.subtitle")}
          </p>

          <div className="admin-sessions-checkout-options">
            {CHECKOUT_REASON_OPTIONS.map((option) => (
              <label key={option.value} className="admin-sessions-checkout-option">
                <input
                  type="radio"
                  name="admin_checkout_reason"
                  value={option.value}
                  checked={checkoutReason === option.value}
                  onChange={(event) => setCheckoutReason(event.target.value)}
                  disabled={Boolean(
                    checkoutConfirmSession &&
                      checkoutInProgressById[checkoutConfirmSession.check_in_id],
                  )}
                />
                <span>{t(option.key)}</span>
              </label>
            ))}
          </div>

          <div className="admin-sessions-export-actions">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={closeCheckoutConfirm}
              disabled={Boolean(
                checkoutConfirmSession &&
                  checkoutInProgressById[checkoutConfirmSession.check_in_id],
              )}
            >
              {t("admin.common.cancel")}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleCheckoutConfirm}
              disabled={Boolean(
                checkoutConfirmSession &&
                  checkoutInProgressById[checkoutConfirmSession.check_in_id],
              )}
            >
              {checkoutConfirmSession &&
              checkoutInProgressById[checkoutConfirmSession.check_in_id]
                ? t("admin.common.processing")
                : t("admin.dashboard.checkOut")}
            </button>
          </div>
        </div>
      </Modal>

      <SessionAuditDrawer
        isOpen={Boolean(auditDrawerSession)}
        onClose={() => setAuditDrawerSession(null)}
      session={auditDrawerSession}
      t={t}
      formatDateTime={formatDateTime}
      formatMeters={formatMeters}
        formatLocationMatchLabel={formatLocationMatchLabel}
      />

      <SessionCorrectionDrawer
        isOpen={Boolean(correctionSessionId)}
        checkInId={correctionSessionId}
        onClose={() => {
          setCorrectionSessionId(null);
        }}
        onSaved={refreshSessions}
        t={t}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Modal
        isOpen={Boolean(deleteSessionTarget)}
        onClose={() => {
          if (deleteSubmitting) return;
          setDeleteSessionTarget(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.sessions.corrections.deleteTitle", { defaultValue: "Delete Session" })}
          message={t("admin.sessions.corrections.deletePrompt", {
            defaultValue: "This will permanently remove the session if it has no linked sales.",
          })}
          variant="danger"
          confirmText={t("admin.common.delete", { defaultValue: "Delete" })}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.deleting", { defaultValue: "Deleting..." })}
          onConfirm={handleDeleteSessionConfirm}
          onCancel={() => {
            if (deleteSubmitting) return;
            setDeleteSessionTarget(null);
          }}
          isLoading={deleteSubmitting}
        />
      </Modal>
    </div>
  );
}
